import type { AdminSelf } from '@saay/api-client';
import {
  formatCount,
  formatLocalDate,
  formatRatePercent,
  formatTimestamp,
  statusLabel,
  toLocalMobile,
} from '@saay/formatting';
import { Money as Amount, KpiCard } from '@saay/blocks';
import { Progress } from '@saay/shadcn/components/progress';
import { CalendarDaysIcon, CoinsIcon, GaugeIcon, PercentIcon } from 'lucide-react';
import {
  AccordionPanel,
  Banner,
  Button,
  EmptyState,
  ErrorState,
  LoadMore,
  Ltr,
  Masked,
  Money,
  Skeleton,
  StatusChip,
  Table,
  Tabs,
  useWindowClass,
} from '../kit.js';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation, useOutletContext, useParams, useSearchParams } from 'react-router';

import type { AdminMerchant, MerchantBranch, MerchantUserAssignment } from '../api.js';
import {
  ActivationReadinessList,
  type ActivationUiAction,
} from '../components/activation-readiness.js';
import { SurfaceSheetSection } from '../components/surface-section.js';
import { DetailHeader } from '../components/detail-header.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { LoginLockAction } from '../components/login-lock.js';
import { useCloseSheet } from '../components/route-sheet.js';
import { formatDays } from '../format.js';
import type { MerchantDetailTab } from '../nav.js';
import { canPerform, canSeePayoutAccount, canUse } from '../permissions.js';
import {
  listStatus,
  useMerchant,
  useMerchantBranches,
  useMerchantUsers,
  usePlanActivations,
} from '../queries.js';
import {
  AddBranchDialog,
  AddUserDialog,
  planLabel,
  ReplacePlansDialog,
} from './merchant-dialogs.js';
import { CapacityActions, PendingApprovals } from './merchant-capacity.js';
import { ScopeMarketingAssignments } from './marketing-assignments.js';
import { CommercialSections } from './merchant-commercial.js';
import { hasLifecycleActions, type LifecycleOpen, LifecycleActions } from './merchant-lifecycle.js';
import { PayoutSection } from './merchant-payout.js';
import { type MerchantCreatedState, readMerchantCreatedState } from './merchant-setup.js';
import { BranchRequestsSection, EditBranchDialog, EditStaffDialog } from './merchant-people.js';
import type { MerchantsOutletContext } from './merchants-list.js';

const notSet = <span className="text-sm text-muted-foreground">هنوز ثبت نشده</span>;

function Facts({ items }: { readonly items: readonly (readonly [string, ReactNode])[] }) {
  return (
    <dl className="saay-facts">
      {items.map(([term, value]) => (
        <div className="admin-fact" key={term}>
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The active installment and wallet-cash plans (D-113). */
function activePlans(merchant: AdminMerchant): string {
  return merchant.activePlanSet.length === 0
    ? 'هیچ طرحی فعال نیست.'
    : merchant.activePlanSet
        .map((plan) => planLabel(plan.type, plan.installmentCount, plan.intervalDays))
        .join('، ');
}

/** Key figures with change actions beside capacity and fee (ADM-17). */
function KeyFigures({
  merchant,
  onCapacityRequest,
  onFeeChange,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly onCapacityRequest: () => void;
  readonly onFeeChange: () => void;
  readonly self: AdminSelf;
}) {
  const { capacity, capacityUsage, saayFeeRate } = merchant;
  const hasCapacity = capacity !== null && capacity.approvedLimitIrr !== 0;
  const canCapacity =
    merchant.status !== 'CLOSED' &&
    !merchant.pendingApprovals.some(
      (approval) => approval.actionType === 'MERCHANT_CAPACITY_CHANGE',
    ) &&
    canUse(self, 'requestCapacityAdjustment');
  const canFee = merchant.status !== 'CLOSED' && canUse(self, 'createCommercialSettingVersion');
  const usage = capacityUsage.utilizationRate;
  return (
    <section
      aria-label="شاخص‌های فروشگاه"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
    >
      <KpiCard
        {...(usage === null
          ? {}
          : {
              foot: (
                <div className="flex w-full flex-col gap-2">
                  <Progress
                    aria-label="مصرف ظرفیت"
                    className={
                      capacityUsage.nearLimit
                        ? '[&_[data-slot=progress-indicator]]:bg-tone-copper-strong'
                        : undefined
                    }
                    value={Math.min(100, Math.max(0, Number(usage) * 100 || 0))}
                  />
                  <span>{`مصرف ظرفیت ${formatRatePercent(usage, { invalid: 'raw' })}`}</span>
                </div>
              ),
            })}
        icon={GaugeIcon}
        label="ظرفیت مصوب"
        tone="forest"
        value={
          <span className="flex items-end justify-between gap-2">
            {hasCapacity ? <Amount rials={capacity.approvedLimitIrr} size="lg" /> : notSet}
            {canCapacity ? (
              <Button
                aria-label="درخواست تغییر ظرفیت"
                onClick={onCapacityRequest}
                size="sm"
                variant="ghost"
              >
                تغییر
              </Button>
            ) : null}
          </span>
        }
      />
      <KpiCard
        icon={CoinsIcon}
        label="اصل بدهی فعال"
        tone="olive"
        value={hasCapacity ? <Amount rials={capacity.unsettledPrincipalIrr} size="lg" /> : notSet}
      />
      <KpiCard
        icon={PercentIcon}
        label="نرخ SAAY"
        tone="gold"
        value={
          <span className="flex items-end justify-between gap-2">
            {saayFeeRate === null ? (
              notSet
            ) : (
              <span className="text-2xl font-extrabold tabular-nums">
                {formatRatePercent(saayFeeRate, { invalid: 'raw' })}
              </span>
            )}
            {canFee ? (
              <Button onClick={onFeeChange} size="sm" variant="ghost">
                تغییر نرخ
              </Button>
            ) : null}
          </span>
        }
      />
      <KpiCard
        icon={CalendarDaysIcon}
        label="طرح فعال"
        tone="clay"
        value={<span className="text-base font-bold">{activePlans(merchant)}</span>}
      />
    </section>
  );
}

/**
 * The prototype «شعب و کاربران» rows: the server counts as they are (`branchCounts`, `liveUserCount`,
 * `merchantAdmins`, AC §18.19.6); the two branch counts are shown side by side, never summed.
 */
function PeopleFigures({ merchant }: { readonly merchant: AdminMerchant }) {
  return (
    <Facts
      items={[
        [
          'شعبه‌ها',
          `${formatCount(merchant.branchCounts.active)} فعال · ${formatCount(merchant.branchCounts.inactive)} غیرفعال`,
        ],
        ['کاربران', `${formatCount(merchant.liveUserCount)} نفر`],
        [
          'ادمین فروشگاه',
          merchant.merchantAdmins.length === 0
            ? notSet
            : merchant.merchantAdmins.map((admin) => admin.displayNameFa ?? 'بدون نام').join('، '),
        ],
      ]}
    />
  );
}

function Readiness({
  merchant,
  onAction,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly onAction: (action: ActivationUiAction) => void;
  readonly self: AdminSelf;
}) {
  if (merchant.status !== 'PENDING_ACTIVATION') {
    return null;
  }
  return (
    <SurfaceSheetSection id="merchant-readiness" title="آمادگی فعال‌سازی">
      <ActivationReadinessList merchant={merchant} onAction={onAction} self={self} />
    </SurfaceSheetSection>
  );
}

function Identity({ merchant }: { readonly merchant: AdminMerchant }) {
  const individual = merchant.legalEntityType === 'INDIVIDUAL';
  const facts = (
    <Facts
      items={[
        ['نام حقوقی', merchant.legalNameFa],
        ['نوع شخصیت', statusLabel('merchantLegalEntityType', merchant.legalEntityType)],
        individual
          ? [
              'کد ملی مالک',
              merchant.ownerNationalCodeMasked === null ? (
                '—'
              ) : (
                <Masked kind="nationalCode" value={merchant.ownerNationalCodeMasked} />
              ),
            ]
          : ['شناسه ملی', merchant.nationalId === null ? '—' : <Ltr>{merchant.nationalId}</Ltr>],
        individual
          ? [
              'شماره جواز کسب',
              merchant.businessLicenseNumber === null ? (
                '—'
              ) : (
                <Ltr>{merchant.businessLicenseNumber}</Ltr>
              ),
            ]
          : [
              'شماره ثبت',
              merchant.registrationNumber === null ? '—' : <Ltr>{merchant.registrationNumber}</Ltr>,
            ],
        [
          'تأیید هویت',
          merchant.kycVerifiedAt === null
            ? notSet
            : formatTimestamp(merchant.kycVerifiedAt, { withTime: false }),
        ],
        ['دسته‌بندی اصلی', merchant.primaryCategory.pathFa],
        [
          'شماره موبایل تماس',
          // M-22: Auditor and Marketing Admin receive only the masked mobile (shown with •).
          merchant.contactMobileE164 === undefined ? (
            <Masked key="mobile" kind="mobile" value={merchant.contactMobileMasked} />
          ) : (
            <Ltr key="mobile">{toLocalMobile(merchant.contactMobileE164)}</Ltr>
          ),
        ],
        ['استان / شهر', `${merchant.province.nameFa} / ${merchant.city.nameFa}`],
        ['آدرس', merchant.addressFa],
        ['تاریخ ثبت', formatTimestamp(merchant.createdAt)],
        ...(merchant.kycVerifiedBy === null
          ? []
          : ([['تأییدکنندهٔ هویت', merchant.kycVerifiedBy.displayNameFa ?? 'بدون نام']] as const)),
        ...(merchant.statusReason === null
          ? []
          : ([['دلیل آخرین تغییر وضعیت', merchant.statusReason]] as const)),
        ...(merchant.suspendedAt === null
          ? []
          : ([['زمان تعلیق', formatTimestamp(merchant.suspendedAt)]] as const)),
        ...(merchant.closedAt === null
          ? []
          : ([['زمان بستن', formatTimestamp(merchant.closedAt)]] as const)),
      ]}
    />
  );
  return (
    <AccordionPanel
      items={[{ content: facts, key: 'identity', title: 'مشخصات و مدارک' }]}
      label="مشخصات فروشگاه"
    />
  );
}

/**
 * Capacity in use and the payout account (prototype «تنظیمات تجاری»): read-only here. The plan's
 * allowed range is shown once, on the plan section (owner fix under D-79), not here as a second
 * «فاصله اقساط».
 */
function Commercial({
  merchant,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly self: AdminSelf;
}) {
  const { capacity, payoutAccount } = merchant;
  const payoutItems: (readonly [string, ReactNode])[] = canSeePayoutAccount(self)
    ? [
        [
          'حساب تسویه',
          payoutAccount === undefined || payoutAccount === null ? (
            notSet
          ) : (
            <span className="admin-inline">
              <Masked kind="sheba" value={payoutAccount.shebaMasked} />
              {` — ${payoutAccount.holderName} `}
              <StatusChip
                enumName="payoutAccountVerificationStatus"
                value={payoutAccount.verificationStatus}
              />
            </span>
          ),
        ],
      ]
    : [];
  const items: (readonly [string, ReactNode])[] = [
    ...(capacity === null || capacity.approvedLimitIrr === 0
      ? []
      : ([
          ['ظرفیت قابل استفاده', <Money key="a" rials={capacity.availableCapacityIrr} />],
          ['رزرو موقت', <Money key="r" rials={capacity.reservedPrincipalIrr} />],
        ] as const)),
    ...payoutItems,
  ];
  if (items.length === 0) {
    return null;
  }
  return (
    <SurfaceSheetSection id="merchant-commercial" title="تنظیمات تجاری">
      <Facts items={items} />
    </SurfaceSheetSection>
  );
}

/**
 * The range the merchant's plan must fit (`effectiveBounds`, AC §17.3.3 rule 3, §23.3), from the
 * platform `installment-bounds` policy. It is a limit for the plan, never a second interval to fill
 * (owner fix under D-79).
 */
export function planRangeText(bounds: AdminMerchant['effectiveBounds']): string {
  return `فاصلهٔ اقساط ${formatDays(bounds.minIntervalDays)} تا ${formatDays(bounds.maxIntervalDays)}؛ تعداد × فاصله حداکثر ${formatDays(bounds.maxTotalDurationDays)}؛ پرداخت آخر ماه دست‌کم ${formatDays(bounds.endOfSaleMonthMinLeadDays)} پیش از پایان ماه`;
}

/** A titled list inside «شعب و کاربران» with its add action. */
function SubBlock({
  action,
  children,
  id,
  title,
}: {
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly id: string;
  readonly title: string;
}) {
  return (
    <section aria-labelledby={id} className="admin-sub-block">
      <div className="saay-sheet-section__head">
        <h4 id={id}>{title}</h4>
        {action}
      </div>
      {children}
    </section>
  );
}

function Branches({
  canAdd,
  canEdit,
  merchant,
}: {
  readonly canAdd: boolean;
  readonly canEdit: boolean;
  readonly merchant: AdminMerchant;
}) {
  const branches = useMerchantBranches(merchant.id);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MerchantBranch | null>(null);
  const rows = branches.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <SubBlock
      action={
        canAdd ? (
          <Button icon="plus" onClick={() => setOpen(true)} size="sm" variant="secondary">
            افزودن شعبه
          </Button>
        ) : undefined
      }
      id="merchant-branches"
      title="شعبه‌ها"
    >
      <QueryState
        empty={<EmptyState icon="store" title="هنوز شعبه‌ای ثبت نشده است" />}
        error={branches.error}
        isEmpty={rows.length === 0}
        onRetry={() => void branches.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری شعبه‌ها…" />}
        status={listStatus(branches)}
      >
        <Table
          caption="شعبه‌های فروشگاه"
          columns={[
            { header: 'نام شعبه', key: 'name', pin: 'start', render: (row) => row.nameFa },
            {
              header: 'استان / شهر',
              key: 'geo',
              render: (row) => `${row.province.nameFa} / ${row.city.nameFa}`,
            },
            { header: 'آدرس', key: 'address', render: (row) => row.addressFa },
            {
              header: 'وضعیت',
              key: 'status',
              render: (row) => <StatusChip enumName="activeStatus" value={row.status} />,
            },
            {
              detail: false,
              header: <span className="saay-visually-hidden">ویرایش</span>,
              key: 'edit',
              pin: 'end',
              render: (row) =>
                canEdit ? (
                  <Button onClick={() => setEditing(row)} size="sm" variant="ghost">
                    ویرایش
                  </Button>
                ) : null,
            },
          ]}
          rowKey={(row) => row.id}
          rows={rows}
        />
        {branches.isFetchNextPageError ? (
          <ErrorState error={branches.error} onRetry={() => void branches.fetchNextPage()} />
        ) : null}
        <LoadMore
          hasMore={branches.hasNextPage}
          loading={branches.isFetchingNextPage}
          onLoadMore={() => void branches.fetchNextPage()}
        />
      </QueryState>
      {canAdd ? <AddBranchDialog merchant={merchant} onOpenChange={setOpen} open={open} /> : null}
      {editing === null ? null : (
        <EditBranchDialog
          branch={editing}
          merchant={merchant}
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
        />
      )}
    </SubBlock>
  );
}

function Users({
  canAdd,
  canEdit,
  merchant,
  self,
}: {
  readonly canAdd: boolean;
  readonly canEdit: boolean;
  readonly merchant: AdminMerchant;
  readonly self: AdminSelf;
}) {
  const users = useMerchantUsers(merchant.id);
  const branches = useMerchantBranches(merchant.id);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MerchantUserAssignment | null>(null);
  const rows = users.data?.pages.flatMap((page) => page.items) ?? [];
  const activeBranches = (branches.data?.pages.flatMap((page) => page.items) ?? []).filter(
    (branch) => branch.status === 'ACTIVE',
  );
  const hasMerchantAdmin = rows.some(
    (row) => row.role === 'MERCHANT_ADMIN' && row.status !== 'DISABLED',
  );
  return (
    <SubBlock
      action={
        canAdd ? (
          <Button
            disabled={users.status !== 'success'}
            icon="plus"
            onClick={() => setOpen(true)}
            size="sm"
            variant="secondary"
          >
            افزودن کاربر
          </Button>
        ) : undefined
      }
      id="merchant-users"
      title="کاربران فروشگاه"
    >
      <QueryState
        empty={<EmptyState icon="users" title="هنوز کاربری دعوت نشده است" />}
        error={users.error}
        isEmpty={rows.length === 0}
        onRetry={() => void users.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری کاربران…" />}
        status={listStatus(users)}
      >
        <Table
          caption="فهرست کاربران فروشگاه"
          columns={[
            {
              header: 'نام',
              key: 'name',
              pin: 'start',
              render: (row) => (
                <span className="saay-cell-main">
                  <strong>{row.user.displayNameFa ?? 'بدون نام'}</strong>
                  <small>
                    <Masked kind="mobile" value={row.user.mobileMasked} />
                  </small>
                </span>
              ),
            },
            { header: 'نقش', key: 'role', render: (row) => statusLabel('fixedRole', row.role) },
            {
              header: 'شعبه‌ها',
              key: 'branches',
              render: (row) =>
                row.branches.length === 0
                  ? 'همه شعبه‌ها'
                  : row.branches.map((branch) => branch.nameFa).join('، '),
            },
            {
              header: 'تاریخ دعوت',
              key: 'created',
              render: (row) => formatTimestamp(row.createdAt, { withTime: false }),
            },
            {
              header: 'وضعیت',
              key: 'status',
              render: (row) => <StatusChip enumName="merchantUserStatus" value={row.status} />,
            },
            {
              detail: false,
              header: <span className="saay-visually-hidden">عملیات</span>,
              key: 'actions',
              pin: 'end',
              render: (row) =>
                row.status === 'DISABLED' ? null : (
                  <span className="admin-inline">
                    {canEdit ? (
                      <Button onClick={() => setEditing(row)} size="sm" variant="ghost">
                        ویرایش
                      </Button>
                    ) : null}
                    <LoginLockAction
                      loginLock={row.loginLock}
                      personLabel={row.user.displayNameFa ?? 'کاربر فروشگاه'}
                      self={self}
                      userId={row.user.id}
                    />
                  </span>
                ),
            },
          ]}
          rowKey={(row) => row.assignmentId}
          rows={rows}
        />
        <LoadMore
          hasMore={users.hasNextPage}
          loading={users.isFetchingNextPage}
          onLoadMore={() => void users.fetchNextPage()}
        />
      </QueryState>
      {editing === null ? null : (
        <EditStaffDialog
          activeBranches={activeBranches}
          merchant={merchant}
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
          user={editing}
        />
      )}
      {canAdd && open ? (
        <AddUserDialog
          activeBranches={activeBranches}
          hasMerchantAdmin={hasMerchantAdmin}
          merchant={merchant}
          onOpenChange={setOpen}
          open={open}
        />
      ) : null}
    </SubBlock>
  );
}

/**
 * The merchant's installment and wallet-cash plans (D-113), with the installment range and history.
 */
function Plans({
  canReplace,
  merchant,
  onOpenHandled,
  openRequested = false,
}: {
  readonly canReplace: boolean;
  readonly merchant: AdminMerchant;
  /** Called once a requested open (from the activation checklist) has been applied. */
  readonly onOpenHandled?: () => void;
  readonly openRequested?: boolean;
}) {
  const plans = usePlanActivations(merchant.id);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!openRequested) return;
    if (canReplace) setOpen(true);
    onOpenHandled?.();
  }, [canReplace, onOpenHandled, openRequested]);
  const rows = plans.data?.pages.flatMap((page) => page.items) ?? [];
  const hasPlan = merchant.activePlanSet.length > 0;
  return (
    <SurfaceSheetSection
      actions={
        canReplace ? (
          <Button onClick={() => setOpen(true)} size="sm" variant="secondary">
            {hasPlan ? 'تغییر طرح' : 'ثبت طرح'}
          </Button>
        ) : undefined
      }
      id="merchant-plans"
      title="طرح اقساطی"
    >
      <p className="admin-muted">{`طرح فعال: ${activePlans(merchant)}`}</p>
      <p className="admin-muted">{`محدودهٔ مجاز طرح: ${planRangeText(merchant.effectiveBounds)}`}</p>
      <QueryState
        empty={<EmptyState icon="card" title="هنوز طرحی برای این فروشگاه ثبت نشده است" />}
        error={plans.error}
        isEmpty={rows.length === 0}
        onRetry={() => void plans.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری طرح‌ها…" />}
        status={listStatus(plans)}
      >
        <Table
          caption="سابقه طرح اقساطی"
          columns={[
            {
              header: 'طرح',
              key: 'plan',
              pin: 'start',
              render: (row) => planLabel(row.type, row.installmentCount, row.intervalDays),
            },
            { header: 'از', key: 'from', render: (row) => formatLocalDate(row.effectiveFrom) },
            {
              header: 'تا',
              key: 'until',
              render: (row) =>
                row.effectiveUntil === null ? 'نامحدود' : formatLocalDate(row.effectiveUntil),
            },
            { header: 'دلیل', key: 'reason', render: (row) => row.reason },
            {
              header: 'وضعیت',
              key: 'status',
              pin: 'end',
              render: (row) => <StatusChip enumName="planActivationStatus" value={row.status} />,
            },
          ]}
          rowKey={(row) => row.activationId}
          rows={rows}
        />
        <LoadMore
          hasMore={plans.hasNextPage}
          loading={plans.isFetchingNextPage}
          onLoadMore={() => void plans.fetchNextPage()}
        />
      </QueryState>
      {canReplace && open ? (
        <ReplacePlansDialog merchant={merchant} onOpenChange={setOpen} open={open} />
      ) : null}
    </SurfaceSheetSection>
  );
}

type MerchantTab = MerchantDetailTab;

function parseMerchantTab(value: string | null, payout: boolean): MerchantTab {
  if (value === 'commercial' || value === 'people') return value;
  if (value === 'payout' && payout) return 'payout';
  return 'overview';
}

function tabLabel(base: string, count: number): string {
  return count > 0 ? `${base} (${formatCount(count)})` : base;
}

/**
 * Merchant detail body: tab panels for overview, commercial, people, and payout (ADM-15, ADM-18).
 */
function MerchantSheetBody({
  header,
  merchant,
  onTabChange,
  self,
  setupNotSaved,
  tab,
}: {
  /** The sticky header without actions; the body adds the lifecycle actions it opens. */
  readonly header: (actions: ReactNode, status: ReactNode) => ReactNode;
  readonly merchant: AdminMerchant;
  readonly onTabChange: (tab: MerchantTab) => void;
  readonly self: AdminSelf;
  /** The configuration items the create wizard could not save (owner fix), listed once. */
  readonly setupNotSaved: MerchantCreatedState | undefined;
  readonly tab: MerchantTab;
}) {
  const [lifecycleOpen, setLifecycleOpen] = useState<LifecycleOpen | null>(null);
  const [capacityOpen, setCapacityOpen] = useState<'history' | 'request' | null>(null);
  const [commercialOpen, setCommercialOpen] = useState<'fee' | false>(false);
  const [planOpen, setPlanOpen] = useState(false);
  const clearPlanOpen = useCallback(() => setPlanOpen(false), []);
  const closed = merchant.status === 'CLOSED';
  const payout = canUse(self, 'listPayoutAccounts');
  const compactHeadline = useWindowClass('expanded') === 'compact';

  useEffect(() => {
    if (lifecycleOpen === null) {
      return undefined;
    }
    const timer = setTimeout(() => setLifecycleOpen(null), 0);
    return () => clearTimeout(timer);
  }, [lifecycleOpen]);

  useEffect(() => {
    if (capacityOpen === null) {
      return undefined;
    }
    const timer = setTimeout(() => setCapacityOpen(null), 0);
    return () => clearTimeout(timer);
  }, [capacityOpen]);

  function onActivationAction(action: ActivationUiAction): void {
    switch (action.kind) {
      case 'capacity':
        setCapacityOpen('request');
        break;
      case 'commercial':
        onTabChange('commercial');
        setCommercialOpen('fee');
        break;
      case 'edit':
        setLifecycleOpen({ kind: 'edit-identity' });
        break;
      case 'kyc':
        setLifecycleOpen({ kind: 'kyc' });
        break;
      case 'payout':
        onTabChange('payout');
        break;
      case 'people-branch':
      case 'people-user':
        onTabChange('people');
        break;
      case 'plans':
        onTabChange('commercial');
        setPlanOpen(true);
        break;
      default:
        break;
    }
  }

  const overview = (
    <div className="admin-sheet-stack">
      <KeyFigures
        merchant={merchant}
        onCapacityRequest={() => setCapacityOpen('request')}
        onFeeChange={() => {
          onTabChange('commercial');
          setCommercialOpen('fee');
        }}
        self={self}
      />
      <PendingApprovals approvals={merchant.pendingApprovals} />
      <Readiness merchant={merchant} onAction={onActivationAction} self={self} />
      {merchant.status === 'ACTIVE' ? null : <Commercial merchant={merchant} self={self} />}
      <Identity merchant={merchant} />
      <CapacityActions merchant={merchant} pendingOpen={capacityOpen} self={self} />
      <ScopeMarketingAssignments merchantId={merchant.id} />
    </div>
  );
  const commercial = (
    <div className="admin-sheet-stack">
      <CommercialSections
        merchant={merchant}
        openNewVersion={commercialOpen}
        onOpenNewVersionHandled={() => setCommercialOpen(false)}
        self={self}
      />
      <Plans
        canReplace={!closed && canPerform(self, 'replaceInstallmentPlanActivations')}
        merchant={merchant}
        onOpenHandled={clearPlanOpen}
        openRequested={planOpen}
      />
    </div>
  );
  const people = (
    <div className="admin-sheet-stack">
      <SurfaceSheetSection id="merchant-people" title="شعب و کاربران">
        <PeopleFigures merchant={merchant} />
        <Branches
          canAdd={!closed && canPerform(self, 'createMerchantBranch')}
          canEdit={!closed && canUse(self, 'updateMerchantBranch')}
          merchant={merchant}
        />
        <Users
          canAdd={!closed && canPerform(self, 'createAdminMerchantUser')}
          canEdit={!closed && canUse(self, 'updateAdminMerchantUser')}
          merchant={merchant}
          self={self}
        />
      </SurfaceSheetSection>
      <BranchRequestsSection merchant={merchant} self={self} />
    </div>
  );
  const panels: Record<MerchantTab, ReactNode> = {
    commercial,
    overview,
    payout: (
      <div className="admin-sheet-stack">
        <PayoutSection merchant={merchant} self={self} />
      </div>
    ),
    people,
  };

  const pendingOverview = merchant.pendingApprovals.length;
  const pendingPeople = merchant.pendingBranchRequestCount;

  return (
    <>
      {header(
        hasLifecycleActions(merchant, self) ? (
          <LifecycleActions
            compactHeadline={compactHeadline}
            merchant={merchant}
            pendingOpen={lifecycleOpen}
            self={self}
          />
        ) : undefined,
        merchant.capacityUsage.nearLimit ? (
          <StatusChip title="نزدیک سقف ظرفیت" tone="warning" />
        ) : null,
      )}
      {merchant.status === 'SUSPENDED' ? (
        <Banner tone="warning">فروشگاه تعلیق شده است؛ فروش جدید ممکن نیست.</Banner>
      ) : null}
      {closed ? <Banner tone="warning">فروشگاه بسته شده است و تغییر نمی‌پذیرد.</Banner> : null}
      {setupNotSaved === undefined ? null : (
        <Banner tone="warning">
          این تنظیمات هنگام ثبت فروشگاه ذخیره نشد؛ آن‌ها را از همین صفحه ثبت کنید:
          <ul className="admin-plain-list">
            {setupNotSaved.setupNotSaved.map((item) => (
              <li key={item.label}>{`${item.label}: ${item.messageFa}`}</li>
            ))}
          </ul>
        </Banner>
      )}
      <h2 className="saay-visually-hidden">بخش‌های فروشگاه</h2>
      <Tabs
        label="بخش‌های فروشگاه"
        onChange={onTabChange}
        panel={panels[tab]}
        tabs={[
          { key: 'overview', label: tabLabel('اطلاعات فروشگاه', pendingOverview) },
          { key: 'commercial', label: 'تنظیمات تجاری' },
          { key: 'people', label: tabLabel('شعب و کاربران', pendingPeople) },
          ...(payout ? [{ key: 'payout' as const, label: 'حساب تسویه' }] : []),
        ]}
        value={tab}
      />
    </>
  );
}

/**
 * `/merchants/:merchantId` (ADM-15, Q7): a full page with sticky tabs in the URL; back keeps list
 * filters. Modal dialogs for edits stay centered on the page.
 */
export function MerchantDetailSheet() {
  const { self } = useOutletContext<MerchantsOutletContext>();
  const { merchantId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const merchant = useMerchant(merchantId);
  const location = useLocation();
  // The create wizard lists here what it could not save (owner fix); a later visit has no state.
  const setupNotSaved = readMerchantCreatedState(location.state, merchant.data);
  const close = useCloseSheet('/merchants', ['tab']);
  const payout = canUse(self, 'listPayoutAccounts');
  const tab = parseMerchantTab(params.get('tab'), payout);
  const name = merchant.data?.displayNameFa ?? 'فروشگاه';
  usePageTitle(name, true, 'پروفایل فروشگاه');

  const setTab = (next: MerchantTab) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'overview') nextParams.delete('tab');
    else nextParams.set('tab', next);
    // Keep the wizard's «not saved» list while the admin moves between tabs to fix it.
    setParams(nextParams, { replace: true, state: location.state });
  };

  useEffect(() => {
    if (!payout && tab === 'payout') setTab('overview');
  }, [payout, tab]);

  const header = (actions?: ReactNode, extraStatus?: ReactNode) => {
    const m = merchant.data;
    const when =
      m === undefined
        ? undefined
        : formatTimestamp(
            m.status === 'ACTIVE' && m.activatedAt !== null ? m.activatedAt : m.createdAt,
            { withTime: false },
          );
    return (
      <DetailHeader
        actions={actions}
        actionsLabel="کارهای فروشگاه"
        back={{ label: 'بازگشت به فهرست', onClick: close }}
        meta={m === undefined ? undefined : `${when ?? ''} · ${m.city.nameFa}`}
        status={
          m === undefined ? undefined : (
            <>
              <StatusChip enumName="merchantStatus" value={m.status} />
              {extraStatus}
            </>
          )
        }
        title={name}
      />
    );
  };

  return (
    <>
      {merchant.data === undefined ? header() : null}
      <QueryState
        error={merchant.error}
        onRetry={() => void merchant.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری فروشگاه…" lines={8} />}
        status={merchant.status}
      >
        {merchant.data === undefined ? null : (
          <div className="admin-merchant-tabs">
            <MerchantSheetBody
              header={header}
              merchant={merchant.data}
              onTabChange={setTab}
              self={self}
              setupNotSaved={setupNotSaved}
              tab={tab}
            />
          </div>
        )}
      </QueryState>
    </>
  );
}
